import { CanUpdateErrorState, ErrorStateMatcher, mixinErrorState } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, UntypedFormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OverbookingGridSlot, OverbookingGridSlotTime } from '../../types';
import { DateTimeString } from '@shared/core/types';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

class MatInputBase {
  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    /** @docs-private */
    public ngControl: NgControl,
    public stateChanges: Subject<void>,
  ) {}
}

export const _MatInputMixinBase = mixinErrorState(MatInputBase);

@DecorateUntilDestroy()
@Component({
  selector: 'crm-overbooking-grid-mat-input',
  templateUrl: './overbooking-grid-mat-input.component.html',
  styleUrls: ['./overbooking-grid-mat-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: OverbookingGridMatInputComponent,
    },
  ],
})
export class OverbookingGridMatInputComponent
  extends _MatInputMixinBase
  implements
    CanUpdateErrorState,
    ControlValueAccessor,
    MatFormFieldControl<OverbookingGridSlot[][]>,
    OnChanges,
    OnInit,
    DoCheck,
    OnDestroy
{
  static nextId = 0;

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(newPlaceholder: string) {
    this._placeholder = newPlaceholder;
    this.stateChanges.next();
  }

  get empty(): boolean {
    return !!this.editorControl.value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(isRequired: boolean) {
    this._required = coerceBooleanProperty(isRequired);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  @Output() slotSelected = new EventEmitter<string>();

  @HostBinding() id = `crm-overbooking-grid-mat-input-${OverbookingGridMatInputComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  gridTimeConfig: string[][] = [];
  selectedSlot: DateTimeString;

  focused = false;
  shouldLabelFloat = true;
  readonly controlType: string = 'crm-overbooking-grid-input';
  editorControl: UntypedFormControl = new UntypedFormControl([]);

  get value(): OverbookingGridSlot[][] {
    return this.editorControl.value;
  }

  set value(newValue: OverbookingGridSlot[][]) {
    this.writeValue(newValue);
  }

  private _destroy$$: Subject<void> = new Subject<void>();
  private _placeholder: string;
  private _required = false;
  private _disabled = false;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private _focusMonitor: FocusMonitor,
    private _elRef: ElementRef,
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl, new Subject<void>());

    if (ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._focusMonitor
      .monitor(this._elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this))
      .subscribe((origin: string) => {
        this.focused = !!origin;

        if (!this.focused) {
          this._onTouched();
        }

        this.stateChanges.next();
      });

    this.editorControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => this.editorControl.enabled),
        takeUntil(this._destroy$$),
      )
      .subscribe((newValue: string) => {
        this._onChange(newValue);
      });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnChanges(): void {
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(_event: MouseEvent): void {}

  writeValue(newValue: OverbookingGridSlot[][]): void {
    let setValue = newValue;
    if (!Array.isArray(newValue)) {
      setValue = [];
    }

    this.editorControl.setValue(setValue);

    this.gridTimeConfig = setValue.map(gridSlotConf => gridSlotConf[0].time.map(time => time.slot));

    this.stateChanges.next();
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = coerceBooleanProperty(isDisabled);

    if (this._disabled) {
      this.editorControl.disable();
    } else {
      this.editorControl.enable();
    }

    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    this._destroy$$.next();
    this._destroy$$.complete();

    this.stateChanges.complete();

    this._focusMonitor.stopMonitoring(this._elRef.nativeElement);
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  selectSlot(slot: OverbookingGridSlotTime): void {
    if (!slot.isAvailable || this.disabled) {
      return;
    }

    this.selectedSlot = slot.startDatetime;
    this.slotSelected.emit(slot.startDatetime);
  }

  unselectSlot(): void {
    this.selectedSlot = null;
  }

  private _onChange: (value: string) => void = () => {};

  private _onTouched = () => {};
}
